import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { ArrowRight, ArrowLeft, Check, ShieldCheck, Sparkles, Info } from "lucide-react";

import Field from "@/components/form/Field";
import MultiSelect from "@/components/form/MultiSelect";
import SectionBanner from "@/components/form/SectionBanner";
import SectionHeading from "@/components/common/SectionHeading";
import Toggle from "@/components/form/Toggle";
import Reveal from "@/components/common/Reveal";
import PageHero from "@/components/layout/PageHero";
import { VisualSection } from "@/components/layout/visualLayout";
import ASSETS from "@/lib/assets";
import api from "@/lib/api";
import { EMAIL_RE, fieldClass } from "@/lib/validation";

/* ============================================================
   STATIC OPTION SETS
   ============================================================ */
const INVESTOR_TYPES = [
  "angel", "operator_angel", "vc", "micro_vc", "family_office",
  "corporate_vc", "corporate_innovation", "lp", "scout", "accelerator",
  "venture_builder", "strategic_partner", "advisor", "other",
];
const STAGES = [
  "idea", "pre_seed", "seed", "late_seed", "series_a", "series_b_plus",
  "growth", "secondary", "not_currently_investing",
];
const CURRENCIES = ["EUR", "USD", "GBP", "CHF"];
const SECTORS = [
  "AI", "Generative AI", "Agentic AI", "SaaS", "B2B Software",
  "Enterprise Software", "FinTech", "InsurTech", "HealthTech", "MedTech",
  "ClimateTech", "GreenTech", "EdTech", "Cybersecurity", "Robotics",
  "Mobility", "IndustrialTech", "DeepTech", "HRTech", "FemTech",
  "Creator Economy", "Consumer", "Marketplace", "E-Commerce", "PropTech",
  "LegalTech", "Gaming", "Media", "Other",
];
const REGIONS = [
  "DACH", "Germany", "Austria", "Switzerland", "EU", "UK", "USA",
  "Turkey", "MENA", "Africa", "Global", "Remote-first", "Only local",
];
const BIZ_MODELS = [
  "B2B", "B2C", "B2B2C", "B2G", "SaaS", "Marketplace", "Hardware",
  "Platform", "API Business", "Data Business", "Subscription",
  "Transaction-based", "Usage-based", "Enterprise Sales", "PLG", "Other",
];
const TRACTION_REQS = [
  "idea_only", "mvp_required", "pilots_required", "first_revenue",
  "mrr_arr", "enterprise_lois", "regulatory_approval", "patents_ip",
  "community_growth",
];
const VALUE_ADD = [
  "go_to_market", "enterprise_sales", "hiring", "international_expansion",
  "ai_expertise", "product_strategy", "legal_regulatory",
  "fundraising_support", "brand_pr", "corporate_access",
  "family_office_network", "government_public_sector", "healthcare_access",
  "fintech_licensing", "technical_due_diligence",
];
const DEALFLOW_FREQ = ["1-2", "3-5", "5-10", "10+", "on_request"];
const TEASER_OPTIONS = ["anonymized_teaser", "deck_direct"];
const COMMS_OPTIONS = [
  "portal_only", "portal_email", "whatsapp_after_clearance", "linkedin_after_reveal",
];
const COUNTRIES = [
  "Germany", "Austria", "Switzerland", "France", "Spain", "Italy",
  "Netherlands", "Belgium", "Denmark", "Sweden", "Norway", "Finland",
  "United Kingdom", "Ireland", "Portugal", "Poland", "Czechia",
  "United States", "Canada", "Turkey", "United Arab Emirates", "Israel",
  "Other",
];
const TIMEZONES = [
  "Europe/Berlin", "Europe/Vienna", "Europe/Zurich", "Europe/Paris",
  "Europe/London", "Europe/Madrid", "Europe/Amsterdam", "Europe/Istanbul",
  "America/New_York", "America/Los_Angeles", "America/Toronto",
  "Asia/Dubai", "Asia/Singapore",
];

const DIVERSITY_FIELDS = [
  "interest_female_led",
  "interest_diverse_teams",
  "interest_women_in_tech",
  "interest_underrepresented",
  "interest_impact_diversity",
  "mentoring_female_founders",
  "speaking_role_model",
];

const STORAGE_KEY = "wit_investor_form_v1";
const TOTAL_STEPS = 5;

/* ============================================================
   Wizard step definitions
   ============================================================ */
// step.required: section keys that must be valid to advance
const WIZARD_STEPS_DEF = [
  { id: 1, labelKey: "step1Label", required: ["basics"] },
  { id: 2, labelKey: "step2Label", required: ["types", "stages", "ticket", "sectors"] },
  { id: 3, labelKey: "step3Label", required: ["geo", "biz", "traction"] },
  { id: 4, labelKey: "step4Label", required: [] },
  { id: 5, labelKey: "step5Label", required: ["consents"] },
];

/* ============================================================
   Initial state
   ============================================================ */
const initialForm = () => ({
  basics: {
    first_name: "", last_name: "", email: "",
    phone: "", linkedin_url: "",
    organization_name: "", organization_website: "",
    role: "", country: "", city: "",
    timezone: "Europe/Berlin",
    language: "de",
  },
  types: [],
  stages: [],
  ticket: {
    first_check_min: "", first_check_max: "",
    currency: "EUR",
    follow_on_possible: false,
    is_lead: false,
    is_follow: false,
    syndicate_possible: false,
    co_invest_possible: false,
  },
  sectors_preferred: [],
  sectors_open: [],
  sectors_excluded: [],
  geo: {
    regions_focus: [],
    needs_local_lead: false,
    international_possible: false,
    network_regions: [],
  },
  business_models: [],
  traction_requirements: [],
  diversity: Object.fromEntries(DIVERSITY_FIELDS.map((k) => [k, false])),
  value_add: [],
  dealflow_pref: {
    frequency: "3-5",
    curated_only: true,
    open_to_early_signals: false,
    teaser_preference: "anonymized_teaser",
    wants_fatma_call_before_reveal: false,
  },
  thesis_text: "",
  comms_pref: {
    primary: "portal_email",
    monthly_digest: true,
    immediate_alerts_high_fit: true,
  },
  consents: {
    truthful_information: false,
    privacy_policy: false,
    curated_proposals: false,
    no_direct_contact: false,
    no_investment_recommendation: false,
    gdpr_data_processing: false,
    nda_confidentiality: false,
    investor_qualification: false,
  },
});

/* ============================================================
   Wizard Progress Bar
   ============================================================ */
function WizardProgress({ wizardStep, steps }) {
  return (
    <div className="flex items-center w-full mb-10" data-testid="wizard-progress">
      {steps.map((step, i) => {
        const isDone = wizardStep > step.id;
        const isActive = wizardStep === step.id;
        return (
          <div key={step.id} className="flex items-center" style={{ flex: i < steps.length - 1 ? "1 1 0%" : "0 0 auto" }}>
            <div className="flex flex-col items-center gap-1.5 shrink-0">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                isDone
                  ? "bg-raspberry text-white"
                  : isActive
                  ? "bg-white border-2 border-raspberry text-raspberry"
                  : "bg-white border border-sandBeige text-warmGrey"
              }`}>
                {isDone ? <Check size={14} strokeWidth={2.8} /> : step.id}
              </div>
              <span className={`hidden sm:block text-xs leading-none text-center whitespace-nowrap transition-colors duration-200 ${
                isActive ? "text-raspberry font-medium" : isDone ? "text-cocoa" : "text-warmGrey"
              }`}>{step.label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-px mx-2 transition-colors duration-300 ${isDone ? "bg-raspberry" : "bg-sandBeige"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function RequestInvestorCall() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [form, setForm] = useState(initialForm);
  const [wizardStep, setWizardStep] = useState(1);
  const [emailConflict, setEmailConflict] = useState(null);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState({ status: "idle", message: "" });
  const [restoredHint, setRestoredHint] = useState(false);
  const [touched,      setTouched]      = useState({});
  const [stepAttempted, setStepAttempted] = useState(false);
  const formTopRef = useRef(null);

  const wizardSteps = WIZARD_STEPS_DEF.map((s) => ({
    ...s,
    label: t(`investor.wizard.${s.labelKey}`),
  }));

  /* ---- localStorage restore on mount ---- */
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.basics) {
        // Merge to ensure new consent keys exist even in old saves
        const merged = {
          ...initialForm(),
          ...parsed,
          consents: { ...initialForm().consents, ...(parsed.consents || {}) },
        };
        setForm(merged);
        setRestoredHint(true);
      }
    } catch (_e) { /* ignore */ }
  }, []);

  /* ---- localStorage autosave (every 5s) ---- */
  useEffect(() => {
    if (state.status === "success") return;
    const id = setInterval(() => {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(form)); }
      catch (_e) { /* quota — ignore */ }
    }, 5000);
    return () => clearInterval(id);
  }, [form, state.status]);

  /* ---- setters ---- */
  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const setBasics = (k, v) => setForm((f) => ({ ...f, basics: { ...f.basics, [k]: v } }));
  const setTicket = (k, v) => setForm((f) => ({ ...f, ticket: { ...f.ticket, [k]: v } }));
  const setGeo = (k, v) => setForm((f) => ({ ...f, geo: { ...f.geo, [k]: v } }));
  const setDiversity = (k, v) => setForm((f) => ({ ...f, diversity: { ...f.diversity, [k]: v } }));
  const setDealflow = (k, v) => setForm((f) => ({ ...f, dealflow_pref: { ...f.dealflow_pref, [k]: v } }));
  const setComms = (k, v) => setForm((f) => ({ ...f, comms_pref: { ...f.comms_pref, [k]: v } }));
  const setConsent = (k, v) => setForm((f) => ({ ...f, consents: { ...f.consents, [k]: v } }));

  /* ---- per-field errors (basics + ticket) ---- */
  const basicsErrs = useMemo(() => {
    const b = form.basics;
    const e = {};
    if (!b.first_name.trim())   e.first_name       = "Pflichtfeld.";
    if (!b.last_name.trim())    e.last_name        = "Pflichtfeld.";
    if (!b.email.trim())        e.email            = "Pflichtfeld.";
    else if (!EMAIL_RE.test(b.email.trim())) e.email = "Ungültige E-Mail.";
    if (!b.linkedin_url.trim()) e.linkedin_url     = "Pflichtfeld.";
    if (!b.organization_name.trim()) e.organization_name = "Pflichtfeld.";
    if (!b.role.trim())         e.role             = "Pflichtfeld.";
    if (!b.country)             e.country          = "Bitte ein Land auswählen.";
    if (!b.city.trim())         e.city             = "Pflichtfeld.";
    return e;
  }, [form.basics]);

  const ticketErrs = useMemo(() => {
    const e = {};
    if (!form.ticket.first_check_min) e.first_check_min = "Pflichtfeld.";
    else if (Number(form.ticket.first_check_min) < 0) e.first_check_min = "Muss positiv sein.";
    if (!form.ticket.first_check_max) e.first_check_max = "Pflichtfeld.";
    else if (Number(form.ticket.first_check_max) < 0) e.first_check_max = "Muss positiv sein.";
    else if (form.ticket.first_check_min && Number(form.ticket.first_check_max) < Number(form.ticket.first_check_min))
      e.first_check_max = "Maximum muss ≥ Minimum sein.";
    return e;
  }, [form.ticket]);

  /** show error for a basics field */
  const showB = (k) => (stepAttempted || touched[k]) && !!basicsErrs[k];
  /** show error for a ticket field */
  const showT = (k) => (stepAttempted || touched[k]) && !!ticketErrs[k];
  /** touch a field */
  const touch = (k) => setTouched((prev) => ({ ...prev, [k]: true }));

  /* ---- section validity ---- */
  const sectionValid = useMemo(() => {
    const b = form.basics;
    return {
      basics: !!(
        b.first_name && b.last_name && b.email &&
        b.linkedin_url && b.organization_name && b.role &&
        b.country && b.city && b.timezone
      ),
      types: form.types.length > 0,
      stages: form.stages.length > 0,
      ticket: !!(
        form.ticket.first_check_min && form.ticket.first_check_max &&
        Number(form.ticket.first_check_max) >= Number(form.ticket.first_check_min)
      ),
      sectors: form.sectors_preferred.length > 0,
      geo: form.geo.regions_focus.length > 0,
      biz: form.business_models.length > 0,
      traction: form.traction_requirements.length > 0,
      diversity: true,
      valueadd: true,
      dealflow: true,
      thesis: true,
      comms: true,
      consents: Object.values(form.consents).every(Boolean),
    };
  }, [form]);

  /* ---- Email duplicate guard on blur ---- */
  const onEmailBlur = useCallback(async () => {
    const email = form.basics.email?.trim().toLowerCase();
    if (!email || !email.includes("@")) { setEmailConflict(null); return; }
    try {
      await api.get(`/investors/check-email/${encodeURIComponent(email)}`);
      setEmailConflict(null);
    } catch (err) {
      if (err?.response?.status === 409) setEmailConflict(t("investor.errorEmailExists"));
      else setEmailConflict(null);
    }
  }, [form.basics.email, t]);

  /* ---- Wizard navigation ---- */
  const scrollTop = () => {
    formTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleNext = () => {
    const required = WIZARD_STEPS_DEF[wizardStep - 1].required;
    const errs = {};
    required.forEach((k) => { if (!sectionValid[k]) errs[k] = true; });
    setErrors(errs);
    if (Object.keys(errs).length) {
      setStepAttempted(true);
      setState({ status: "error", message: t("investor.errorMissing") });
      return;
    }
    setStepAttempted(false);
    setTouched({});
    setState({ status: "idle", message: "" });
    setWizardStep((s) => s + 1);
    scrollTop();
  };

  const handleBack = () => {
    setErrors({});
    setStepAttempted(false);
    setTouched({});
    setState({ status: "idle", message: "" });
    setWizardStep((s) => s - 1);
    scrollTop();
  };

  /* ---- Final submit ---- */
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!sectionValid.consents) {
      setErrors({ consents: true });
      setState({ status: "error", message: t("investor.errorConsents") });
      return;
    }

    setState({ status: "loading", message: "" });

    const payload = {
      basics: {
        first_name: form.basics.first_name.trim(),
        last_name: form.basics.last_name.trim(),
        email: form.basics.email.trim().toLowerCase(),
        phone: form.basics.phone?.trim() || null,
        linkedin_url: form.basics.linkedin_url.trim(),
        organization_name: form.basics.organization_name.trim(),
        organization_website: form.basics.organization_website?.trim() || null,
        role: form.basics.role.trim(),
        country: form.basics.country,
        city: form.basics.city.trim(),
        timezone: form.basics.timezone,
        language: form.basics.language,
      },
      types: form.types,
      stages: form.stages,
      ticket: {
        first_check_min: Number(form.ticket.first_check_min),
        first_check_max: Number(form.ticket.first_check_max),
        currency: form.ticket.currency,
        follow_on_possible: form.ticket.follow_on_possible,
        is_lead: form.ticket.is_lead,
        is_follow: form.ticket.is_follow,
        syndicate_possible: form.ticket.syndicate_possible,
        co_invest_possible: form.ticket.co_invest_possible,
      },
      sectors_preferred: form.sectors_preferred,
      sectors_open: form.sectors_open,
      sectors_excluded: form.sectors_excluded,
      geo: form.geo,
      business_models: form.business_models,
      traction_requirements: form.traction_requirements,
      diversity: form.diversity,
      value_add: form.value_add,
      dealflow_pref: form.dealflow_pref,
      thesis_text: form.thesis_text?.trim() || null,
      comms_pref: form.comms_pref,
      consents: form.consents,
      language: i18n.language?.startsWith("en") ? "en" : "de",
    };

    try {
      await api.post("/investors/submit", payload);
      try { localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* ignore */ }
      setState({ status: "success", message: "" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      let message = t("investor.errorGeneric");
      if (err?.response?.status === 429) message = t("investor.errorRateLimit");
      if (err?.response?.status === 409) {
        message = t("investor.errorEmailExists");
        setEmailConflict(message);
        setWizardStep(1);
        scrollTop();
      }
      if (err?.response?.status === 422) message = t("investor.errorValidation");
      setState({ status: "error", message });
    }
  };

  /* ============================================================
     Render — success state
     ============================================================ */
  if (state.status === "success") {
    return (
      <div data-testid="investor-success">
        <VisualSection testId="investor-success-card">
          <Reveal>
            <div className="max-w-2xl mx-auto wit-card text-center flex flex-col items-center gap-6">
              <div className="w-14 h-14 rounded-full bg-raspberry text-white flex items-center justify-center">
                <Check size={22} />
              </div>
              <SectionHeading
                title={t("investor.successTitle")}
                subtitle={t("investor.successBody")}
                align="center"
              />
              <Link to="/" className="wit-btn-primary" data-testid="investor-success-home">
                {t("investor.successCta")} <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </VisualSection>
      </div>
    );
  }

  /* ============================================================
     Render — form
     ============================================================ */
  return (
    <div data-testid="page-request-investor-call">
      <PageHero
        eyebrow={t("investor.eyebrow")}
        title={t("investor.title")}
        subtitle={t("investor.lead")}
        image={ASSETS.editorial[2]}
        testId="investor-hero"
      />

      <div className="wit-section-wide pb-24" ref={formTopRef}>

        {restoredHint && (
          <div className="mb-6 bg-butterCream/70 border border-butterCream rounded-2xl px-5 py-3 text-sm text-cocoa flex items-start gap-3" data-testid="restored-hint">
            <Info size={16} className="mt-0.5 text-raspberry shrink-0" />
            <p className="leading-relaxed">{t("investor.restoredHint")}</p>
            <button
              type="button"
              onClick={() => {
                setForm(initialForm());
                try { localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* ignore */ }
                setRestoredHint(false);
              }}
              className="ml-auto text-xs underline text-warmGrey hover:text-raspberry"
              data-testid="restored-clear"
            >
              {t("investor.restoredClear")}
            </button>
          </div>
        )}

        {/* Wizard progress */}
        <WizardProgress wizardStep={wizardStep} steps={wizardSteps} />

        {/* Step label */}
        <p className="wit-eyebrow text-warmGrey mb-6" data-testid="wizard-step-label">
          {t("investor.wizard.stepOf", { current: wizardStep, total: TOTAL_STEPS })}
          {wizardStep === 3 && (
            <span className="ml-2 text-warmGrey/60">— {t("investor.wizard.step3note")}</span>
          )}
        </p>

        <form
          onSubmit={onSubmit}
          className="flex flex-col gap-10 w-full"
          data-testid="investor-form"
        >
          {/* ============ STEP 1 — BASICS ============ */}
          {wizardStep === 1 && (
            <section data-testid="section-basics" className="flex flex-col gap-6">
              <SectionBanner
                eyebrow={t("investor.sectionShort.basics")}
                title={t("investor.sectionA.title")}
                hint={t("investor.sectionA.hint")}
                accent="bg-roseMilk"
              />
              <div className="wit-card grid sm:grid-cols-2 gap-5">
                <Field label={t("investor.basics.firstName")} required
                  error={showB("first_name") ? basicsErrs.first_name : null} testId="field-first-name">
                  <input
                    className={fieldClass("wit-input", showB("first_name"), !!form.basics.first_name.trim() && !basicsErrs.first_name)}
                    value={form.basics.first_name}
                    onChange={(e) => setBasics("first_name", e.target.value)}
                    onBlur={() => touch("first_name")}
                    data-testid="input-first-name" />
                </Field>
                <Field label={t("investor.basics.lastName")} required
                  error={showB("last_name") ? basicsErrs.last_name : null} testId="field-last-name">
                  <input
                    className={fieldClass("wit-input", showB("last_name"), !!form.basics.last_name.trim() && !basicsErrs.last_name)}
                    value={form.basics.last_name}
                    onChange={(e) => setBasics("last_name", e.target.value)}
                    onBlur={() => touch("last_name")}
                    data-testid="input-last-name" />
                </Field>
                <Field label={t("investor.basics.email")} required
                  error={showB("email") ? basicsErrs.email : emailConflict} testId="field-email">
                  <input
                    className={fieldClass("wit-input", showB("email") || !!emailConflict, !!form.basics.email.trim() && !basicsErrs.email && !emailConflict)}
                    type="email"
                    value={form.basics.email}
                    onChange={(e) => setBasics("email", e.target.value)}
                    onBlur={() => { touch("email"); onEmailBlur(); }}
                    data-testid="input-email" />
                </Field>
                <Field label={t("investor.basics.phone")} testId="field-phone">
                  <input className="wit-input" value={form.basics.phone}
                    onChange={(e) => setBasics("phone", e.target.value)}
                    data-testid="input-phone" />
                </Field>
                <Field label={t("investor.basics.linkedin")} required hint="https://linkedin.com/in/…"
                  error={showB("linkedin_url") ? basicsErrs.linkedin_url : null} testId="field-linkedin">
                  <input
                    className={fieldClass("wit-input", showB("linkedin_url"), !!form.basics.linkedin_url.trim() && !basicsErrs.linkedin_url)}
                    type="url"
                    value={form.basics.linkedin_url}
                    onChange={(e) => setBasics("linkedin_url", e.target.value)}
                    onBlur={() => touch("linkedin_url")}
                    data-testid="input-linkedin" />
                </Field>
                <Field label={t("investor.basics.role")} required
                  error={showB("role") ? basicsErrs.role : null} testId="field-role">
                  <input
                    className={fieldClass("wit-input", showB("role"), !!form.basics.role.trim() && !basicsErrs.role)}
                    placeholder="Partner, GP, Associate, …"
                    value={form.basics.role}
                    onChange={(e) => setBasics("role", e.target.value)}
                    onBlur={() => touch("role")}
                    data-testid="input-role" />
                </Field>
                <Field label={t("investor.basics.organization")} required span={2}
                  error={showB("organization_name") ? basicsErrs.organization_name : null} testId="field-org">
                  <input
                    className={fieldClass("wit-input", showB("organization_name"), !!form.basics.organization_name.trim() && !basicsErrs.organization_name)}
                    value={form.basics.organization_name}
                    onChange={(e) => setBasics("organization_name", e.target.value)}
                    onBlur={() => touch("organization_name")}
                    data-testid="input-organization" />
                </Field>
                <Field label={t("investor.basics.website")} span={2} testId="field-website">
                  <input className="wit-input" type="url" placeholder="https://…" value={form.basics.organization_website}
                    onChange={(e) => setBasics("organization_website", e.target.value)}
                    data-testid="input-website" />
                </Field>
                <Field label={t("investor.basics.country")} required
                  error={showB("country") ? basicsErrs.country : null} testId="field-country">
                  <select
                    className={fieldClass("wit-input", showB("country"), !!form.basics.country && !basicsErrs.country)}
                    value={form.basics.country}
                    onChange={(e) => setBasics("country", e.target.value)}
                    onBlur={() => touch("country")}
                    data-testid="input-country">
                    <option value="">{t("investor.basics.countryPlaceholder")}</option>
                    {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label={t("investor.basics.city")} required
                  error={showB("city") ? basicsErrs.city : null} testId="field-city">
                  <input
                    className={fieldClass("wit-input", showB("city"), !!form.basics.city.trim() && !basicsErrs.city)}
                    value={form.basics.city}
                    onChange={(e) => setBasics("city", e.target.value)}
                    onBlur={() => touch("city")}
                    data-testid="input-city" />
                </Field>
                <Field label={t("investor.basics.timezone")} required testId="field-timezone">
                  <select className="wit-input" value={form.basics.timezone}
                    onChange={(e) => setBasics("timezone", e.target.value)}
                    data-testid="input-timezone">
                    {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                  </select>
                </Field>
                <Field label={t("investor.basics.language")} required testId="field-language">
                  <div className="flex gap-2">
                    {["de", "en"].map((lng) => {
                      const sel = form.basics.language === lng;
                      return (
                        <button type="button" key={lng}
                          onClick={() => setBasics("language", lng)}
                          className={`wit-pill transition-colors ${sel ? "bg-raspberry text-white border-raspberry" : "bg-white border-sandBeige text-cocoa hover:border-raspberry"}`}
                          data-testid={`input-language-${lng}`}>
                          {lng.toUpperCase()}
                        </button>
                      );
                    })}
                  </div>
                </Field>
              </div>
              {errors.basics && (
                <p className="text-coral text-sm" role="alert">{t("investor.errorMissing")}</p>
              )}
            </section>
          )}

          {/* ============ STEP 2 — INVESTMENT FOCUS ============ */}
          {wizardStep === 2 && (
            <div className="flex flex-col gap-10">
              {/* B: Types */}
              <section data-testid="section-types" className="flex flex-col gap-6">
                <SectionBanner
                  eyebrow={t("investor.sectionShort.types")}
                  title={t("investor.sectionB.title")}
                  hint={t("investor.sectionB.hint")}
                  accent="bg-skyMist"
                />
                <div className="wit-card">
                  <Field label={t("investor.sectionB.label")} required
                    error={errors.types ? t("investor.errorMissingSection") : null}
                    testId="field-types">
                    <MultiSelect
                      options={INVESTOR_TYPES.map((k) => ({ key: k, label: t(`investor.types.${k}`) }))}
                      value={form.types}
                      onChange={(v) => set("types", v)}
                      testId="ms-types"
                    />
                  </Field>
                </div>
              </section>

              {/* C: Stages */}
              <section data-testid="section-stages" className="flex flex-col gap-6">
                <SectionBanner
                  eyebrow={t("investor.sectionShort.stages")}
                  title={t("investor.sectionC.title")}
                  hint={t("investor.sectionC.hint")}
                  accent="bg-lavenderFog"
                />
                <div className="wit-card">
                  <Field label={t("investor.sectionC.label")} required
                    error={errors.stages ? t("investor.errorMissingSection") : null}
                    testId="field-stages">
                    <MultiSelect
                      options={STAGES.map((k) => ({ key: k, label: t(`investor.stages.${k}`) }))}
                      value={form.stages}
                      onChange={(v) => set("stages", v)}
                      testId="ms-stages"
                    />
                  </Field>
                </div>
              </section>

              {/* D: Ticket */}
              <section data-testid="section-ticket" className="flex flex-col gap-6">
                <SectionBanner
                  eyebrow={t("investor.sectionShort.ticket")}
                  title={t("investor.sectionD.title")}
                  hint={t("investor.sectionD.hint")}
                  accent="bg-butterCream"
                />
                <div className="wit-card grid sm:grid-cols-2 gap-5">
                  <Field label={t("investor.ticket.min")} required
                    error={showT("first_check_min") ? ticketErrs.first_check_min : null} testId="field-ticket-min">
                    <input
                      className={fieldClass("wit-input", showT("first_check_min"), !!form.ticket.first_check_min && !ticketErrs.first_check_min)}
                      type="number" min="0"
                      value={form.ticket.first_check_min}
                      onChange={(e) => setTicket("first_check_min", e.target.value)}
                      onBlur={() => touch("first_check_min")}
                      data-testid="input-ticket-min" />
                  </Field>
                  <Field label={t("investor.ticket.max")} required
                    error={showT("first_check_max") ? ticketErrs.first_check_max : null} testId="field-ticket-max">
                    <input
                      className={fieldClass("wit-input", showT("first_check_max"), !!form.ticket.first_check_max && !ticketErrs.first_check_max)}
                      type="number" min="0"
                      value={form.ticket.first_check_max}
                      onChange={(e) => setTicket("first_check_max", e.target.value)}
                      onBlur={() => touch("first_check_max")}
                      data-testid="input-ticket-max" />
                  </Field>
                  <Field label={t("investor.ticket.currency")} required testId="field-currency">
                    <select className="wit-input" value={form.ticket.currency}
                      onChange={(e) => setTicket("currency", e.target.value)}
                      data-testid="input-currency">
                      {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                  <div className="sm:col-span-2 grid sm:grid-cols-2 gap-3 mt-2">
                    {[
                      ["follow_on_possible", "ticket.followOn"],
                      ["is_lead", "ticket.isLead"],
                      ["is_follow", "ticket.isFollow"],
                      ["syndicate_possible", "ticket.syndicate"],
                      ["co_invest_possible", "ticket.coInvest"],
                    ].map(([key, lk]) => (
                      <Toggle
                        key={key}
                        checked={!!form.ticket[key]}
                        onChange={(v) => setTicket(key, v)}
                        label={t(`investor.${lk}`)}
                        testId={`toggle-${key}`}
                      />
                    ))}
                  </div>
                  {errors.ticket && (
                    <p className="sm:col-span-2 text-xs text-coral" role="alert" data-testid="ticket-error">
                      {t("investor.errorTicketRange")}
                    </p>
                  )}
                </div>
              </section>

              {/* E: Sectors */}
              <section data-testid="section-sectors" className="flex flex-col gap-6">
                <SectionBanner
                  eyebrow={t("investor.sectionShort.sectors")}
                  title={t("investor.sectionE.title")}
                  hint={t("investor.sectionE.hint")}
                  accent="bg-sageMint"
                />
                <div className="wit-card flex flex-col gap-6">
                  <Field label={t("investor.sectionE.preferred")} required
                    error={errors.sectors ? t("investor.errorMissingSection") : null}
                    testId="field-sectors-preferred">
                    <MultiSelect
                      options={SECTORS.map((k) => ({ key: k, label: k }))}
                      value={form.sectors_preferred}
                      onChange={(v) => set("sectors_preferred", v)}
                      testId="ms-sectors-preferred"
                    />
                  </Field>
                  <Field label={t("investor.sectionE.open")} hint={t("investor.sectionE.openHint")} testId="field-sectors-open">
                    <MultiSelect
                      options={SECTORS.map((k) => ({ key: k, label: k }))}
                      value={form.sectors_open}
                      onChange={(v) => set("sectors_open", v)}
                      testId="ms-sectors-open"
                    />
                  </Field>
                  <Field label={t("investor.sectionE.excluded")} hint={t("investor.sectionE.excludedHint")} testId="field-sectors-excluded">
                    <MultiSelect
                      options={SECTORS.map((k) => ({ key: k, label: k }))}
                      value={form.sectors_excluded}
                      onChange={(v) => set("sectors_excluded", v)}
                      testId="ms-sectors-excluded"
                    />
                  </Field>
                </div>
              </section>
            </div>
          )}

          {/* ============ STEP 3 — DETAILS (optional enrichment) ============ */}
          {wizardStep === 3 && (
            <div className="flex flex-col gap-10">
              {/* F: Geo */}
              <section data-testid="section-geo" className="flex flex-col gap-6">
                <SectionBanner
                  eyebrow={t("investor.sectionShort.geo")}
                  title={t("investor.sectionF.title")}
                  hint={t("investor.sectionF.hint")}
                  accent="bg-skyMist"
                />
                <div className="wit-card flex flex-col gap-6">
                  <Field label={t("investor.sectionF.focus")} required
                    error={errors.geo ? t("investor.errorMissingSection") : null}
                    testId="field-regions-focus">
                    <MultiSelect
                      options={REGIONS.map((k) => ({ key: k, label: k }))}
                      value={form.geo.regions_focus}
                      onChange={(v) => setGeo("regions_focus", v)}
                      testId="ms-regions-focus"
                    />
                  </Field>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Toggle
                      checked={form.geo.needs_local_lead}
                      onChange={(v) => setGeo("needs_local_lead", v)}
                      label={t("investor.geo.needsLocalLead")}
                      testId="toggle-needs-local-lead"
                    />
                    <Toggle
                      checked={form.geo.international_possible}
                      onChange={(v) => setGeo("international_possible", v)}
                      label={t("investor.geo.internationalPossible")}
                      testId="toggle-international-possible"
                    />
                  </div>
                  <Field label={t("investor.geo.network")} hint={t("investor.geo.networkHint")} testId="field-network-regions">
                    <MultiSelect
                      options={REGIONS.map((k) => ({ key: k, label: k }))}
                      value={form.geo.network_regions}
                      onChange={(v) => setGeo("network_regions", v)}
                      testId="ms-network-regions"
                    />
                  </Field>
                </div>
              </section>

              {/* G: Business Models */}
              <section data-testid="section-biz" className="flex flex-col gap-6">
                <SectionBanner
                  eyebrow={t("investor.sectionShort.biz")}
                  title={t("investor.sectionG.title")}
                  hint={t("investor.sectionG.hint")}
                  accent="bg-roseMilk"
                />
                <div className="wit-card">
                  <Field label={t("investor.sectionG.label")} required
                    error={errors.biz ? t("investor.errorMissingSection") : null}
                    testId="field-biz">
                    <MultiSelect
                      options={BIZ_MODELS.map((k) => ({ key: k, label: k }))}
                      value={form.business_models}
                      onChange={(v) => set("business_models", v)}
                      testId="ms-biz"
                    />
                  </Field>
                </div>
              </section>

              {/* H: Traction */}
              <section data-testid="section-traction" className="flex flex-col gap-6">
                <SectionBanner
                  eyebrow={t("investor.sectionShort.traction")}
                  title={t("investor.sectionH.title")}
                  hint={t("investor.sectionH.hint")}
                  accent="bg-lavenderFog"
                />
                <div className="wit-card">
                  <Field label={t("investor.sectionH.label")} required
                    error={errors.traction ? t("investor.errorMissingSection") : null}
                    testId="field-traction">
                    <MultiSelect
                      options={TRACTION_REQS.map((k) => ({
                        key: k, label: t(`investor.traction.${k}`),
                      }))}
                      value={form.traction_requirements}
                      onChange={(v) => set("traction_requirements", v)}
                      testId="ms-traction"
                    />
                  </Field>
                </div>
              </section>

              {/* I: Diversity */}
              <section data-testid="section-diversity" className="flex flex-col gap-6">
                <SectionBanner
                  eyebrow={t("investor.sectionShort.diversity")}
                  title={t("investor.sectionI.title")}
                  hint={t("investor.sectionI.hint")}
                  accent="bg-roseMilk"
                />
                <div className="wit-card grid sm:grid-cols-2 gap-3">
                  {DIVERSITY_FIELDS.map((k) => (
                    <Toggle
                      key={k}
                      checked={!!form.diversity[k]}
                      onChange={(v) => setDiversity(k, v)}
                      label={t(`investor.diversity.${k}`)}
                      testId={`toggle-${k}`}
                    />
                  ))}
                </div>
              </section>

              {/* J: Value Add */}
              <section data-testid="section-valueadd" className="flex flex-col gap-6">
                <SectionBanner
                  eyebrow={t("investor.sectionShort.valueadd")}
                  title={t("investor.sectionJ.title")}
                  hint={t("investor.sectionJ.hint")}
                  accent="bg-butterCream"
                />
                <div className="wit-card">
                  <Field label={t("investor.sectionJ.label")} hint={t("investor.sectionJ.labelHint")} testId="field-valueadd">
                    <MultiSelect
                      options={VALUE_ADD.map((k) => ({
                        key: k, label: t(`investor.valueAdd.${k}`),
                      }))}
                      value={form.value_add}
                      onChange={(v) => set("value_add", v)}
                      testId="ms-valueadd"
                    />
                  </Field>
                </div>
              </section>

              {/* K: Dealflow */}
              <section data-testid="section-dealflow" className="flex flex-col gap-6">
                <SectionBanner
                  eyebrow={t("investor.sectionShort.dealflow")}
                  title={t("investor.sectionK.title")}
                  hint={t("investor.sectionK.hint")}
                  accent="bg-sageMint"
                />
                <div className="wit-card flex flex-col gap-6">
                  <Field label={t("investor.dealflow.frequency")} required testId="field-frequency">
                    <div className="flex flex-wrap gap-2">
                      {DEALFLOW_FREQ.map((k) => {
                        const sel = form.dealflow_pref.frequency === k;
                        return (
                          <button type="button" key={k}
                            onClick={() => setDealflow("frequency", k)}
                            className={`wit-pill transition-colors ${sel ? "bg-raspberry text-white border-raspberry" : "bg-white border-sandBeige text-cocoa hover:border-raspberry"}`}
                            data-testid={`frequency-${k}`}>
                            {t(`investor.dealflow.frequencies.${k}`)}
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                  <Field label={t("investor.dealflow.teaserLabel")} hint={t("investor.dealflow.teaserHint")} testId="field-teaser">
                    <div className="flex flex-col sm:flex-row gap-2">
                      {TEASER_OPTIONS.map((k) => {
                        const sel = form.dealflow_pref.teaser_preference === k;
                        return (
                          <button type="button" key={k}
                            onClick={() => setDealflow("teaser_preference", k)}
                            className={`wit-pill text-left transition-colors ${sel ? "bg-raspberry text-white border-raspberry" : "bg-white border-sandBeige text-cocoa hover:border-raspberry"}`}
                            data-testid={`teaser-${k}`}>
                            {t(`investor.dealflow.teaser.${k}`)}
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Toggle
                      checked={form.dealflow_pref.curated_only}
                      onChange={(v) => setDealflow("curated_only", v)}
                      label={t("investor.dealflow.curatedOnly")}
                      testId="toggle-curated-only"
                    />
                    <Toggle
                      checked={form.dealflow_pref.open_to_early_signals}
                      onChange={(v) => setDealflow("open_to_early_signals", v)}
                      label={t("investor.dealflow.earlySignals")}
                      testId="toggle-early-signals"
                    />
                    <Toggle
                      checked={form.dealflow_pref.wants_fatma_call_before_reveal}
                      onChange={(v) => setDealflow("wants_fatma_call_before_reveal", v)}
                      label={t("investor.dealflow.wantsFatmaCall")}
                      testId="toggle-wants-fatma-call"
                    />
                  </div>
                </div>
              </section>
            </div>
          )}

          {/* ============ STEP 4 — THESIS & COMMS ============ */}
          {wizardStep === 4 && (
            <div className="flex flex-col gap-10">
              {/* L: Thesis */}
              <section data-testid="section-thesis" className="flex flex-col gap-6">
                <SectionBanner
                  eyebrow={t("investor.sectionShort.thesis")}
                  title={t("investor.sectionL.title")}
                  hint={t("investor.sectionL.hint")}
                  accent="bg-lavenderFog"
                />
                <div className="wit-card flex flex-col gap-3">
                  <Field label={t("investor.sectionL.label")} hint={t("investor.sectionL.labelHint")} testId="field-thesis">
                    <textarea
                      className="wit-input resize-none"
                      rows={6}
                      maxLength={2000}
                      value={form.thesis_text}
                      onChange={(e) => set("thesis_text", e.target.value)}
                      placeholder={t("investor.sectionL.placeholder")}
                      data-testid="input-thesis"
                    />
                    <span className="text-xs text-warmGrey">{form.thesis_text.length} / 2000</span>
                  </Field>
                  <div className="flex items-start gap-3 bg-lavenderFog/50 rounded-2xl px-4 py-3 text-sm text-cocoa">
                    <Sparkles size={16} className="text-raspberry mt-0.5 shrink-0" />
                    <p className="leading-relaxed">{t("investor.sectionL.note")}</p>
                  </div>
                </div>
              </section>

              {/* M: Comms */}
              <section data-testid="section-comms" className="flex flex-col gap-6">
                <SectionBanner
                  eyebrow={t("investor.sectionShort.comms")}
                  title={t("investor.sectionM.title")}
                  hint={t("investor.sectionM.hint")}
                  accent="bg-skyMist"
                />
                <div className="wit-card flex flex-col gap-6">
                  <Field label={t("investor.comms.primary")} testId="field-comms">
                    <div className="flex flex-col gap-2">
                      {COMMS_OPTIONS.map((k) => {
                        const sel = form.comms_pref.primary === k;
                        return (
                          <button type="button" key={k}
                            onClick={() => setComms("primary", k)}
                            className={`flex items-start gap-3 rounded-2xl px-4 py-3 border text-left transition-colors ${sel ? "bg-roseMilk border-raspberry" : "bg-porcelain border-sandBeige hover:border-raspberry"}`}
                            data-testid={`comms-${k}`}>
                            <span className={`mt-1 w-4 h-4 rounded-full border-2 shrink-0 ${sel ? "bg-raspberry border-raspberry" : "border-warmGrey"}`} />
                            <span className="flex-1">
                              <span className="block text-sm font-medium text-cocoa">{t(`investor.comms.options.${k}`)}</span>
                              <span className="block text-xs text-warmGrey mt-0.5">{t(`investor.comms.optionsHint.${k}`)}</span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Toggle
                      checked={form.comms_pref.monthly_digest}
                      onChange={(v) => setComms("monthly_digest", v)}
                      label={t("investor.comms.monthlyDigest")}
                      testId="toggle-monthly-digest"
                    />
                    <Toggle
                      checked={form.comms_pref.immediate_alerts_high_fit}
                      onChange={(v) => setComms("immediate_alerts_high_fit", v)}
                      label={t("investor.comms.immediateAlerts")}
                      testId="toggle-immediate-alerts"
                    />
                  </div>
                </div>
              </section>
            </div>
          )}

          {/* ============ STEP 5 — REGULATORY & CONSENTS ============ */}
          {wizardStep === 5 && (
            <div className="flex flex-col gap-10">
              <section data-testid="section-consents" className="flex flex-col gap-6">
                <SectionBanner
                  eyebrow={t("investor.sectionShort.consents")}
                  title={t("investor.sectionN.title")}
                  hint={t("investor.sectionN.hint")}
                  accent="bg-roseMilk"
                />
                <div className="wit-card flex flex-col gap-3">
                  {[
                    ["truthful_information", "consent1"],
                    ["privacy_policy", "consent2", true],
                    ["curated_proposals", "consent3"],
                    ["no_direct_contact", "consent4"],
                    ["no_investment_recommendation", "consent5"],
                    ["gdpr_data_processing", "consent6"],
                    ["nda_confidentiality", "consent7"],
                    ["investor_qualification", "consent8"],
                  ].map(([key, k, withLink]) => (
                    <label key={key}
                      className={`flex items-start gap-3 rounded-2xl px-4 py-3 border text-sm text-cocoa cursor-pointer transition-colors ${
                        errors.consents && !form.consents[key]
                          ? "bg-red-50 border-red-300"
                          : form.consents[key]
                          ? "bg-green-50 border-green-300"
                          : "bg-porcelain border-sandBeige"
                      }`}>
                      <input type="checkbox" className="accent-raspberry mt-1"
                        checked={!!form.consents[key]}
                        onChange={(e) => setConsent(key, e.target.checked)}
                        data-testid={`consent-${key}`} />
                      <span className="flex-1 leading-relaxed">
                        {withLink ? (
                          <Trans i18nKey={`investor.consents.${k}`}
                            components={[<Link key="0" to="/privacy" className="underline hover:text-raspberry">privacy</Link>]} />
                        ) : (
                          t(`investor.consents.${k}`)
                        )}
                      </span>
                    </label>
                  ))}
                  {errors.consents && (
                    <p className="text-xs text-coral" role="alert" data-testid="consents-error">
                      {t("investor.errorConsents")}
                    </p>
                  )}
                </div>
              </section>

              {/* Trust box */}
              <section>
                <div className="wit-card flex flex-col sm:flex-row gap-5 items-start bg-skyMist/60 border-skyMist">
                  <div className="w-12 h-12 rounded-2xl bg-white border border-cardEdge flex items-center justify-center text-raspberry shrink-0">
                    <ShieldCheck size={20} strokeWidth={1.6} />
                  </div>
                  <div>
                    <p className="font-serif text-xl text-cocoa">{t("investor.trustTitle")}</p>
                    <p className="mt-2 text-sm text-cocoa/80 leading-relaxed">{t("investor.trustBody")}</p>
                  </div>
                </div>
              </section>
            </div>
          )}

          {/* ============ ERROR ============ */}
          {state.status === "error" && state.message && (
            <p className="text-coral text-sm" role="alert" data-testid="investor-error">
              {state.message}
            </p>
          )}

          {/* ============ NAVIGATION ============ */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-2">
            {wizardStep > 1 && (
              <button
                type="button"
                onClick={handleBack}
                className="wit-btn-secondary"
                data-testid="wizard-back"
              >
                <ArrowLeft size={16} /> {t("investor.wizard.back")}
              </button>
            )}

            {wizardStep < TOTAL_STEPS ? (
              <button
                type="button"
                onClick={handleNext}
                className="wit-btn-primary"
                data-testid="wizard-next"
              >
                {t("investor.wizard.next")} <ArrowRight size={16} />
              </button>
            ) : (
              <button
                type="submit"
                disabled={state.status === "loading"}
                className="wit-btn-primary disabled:opacity-60"
                data-testid="investor-submit"
              >
                {state.status === "loading"
                  ? t("investor.submitting")
                  : <>{t("investor.submitButton")} <ArrowRight size={16} /></>
                }
              </button>
            )}

            {wizardStep === 1 && (
              <button
                type="button"
                onClick={() => navigate("/investors")}
                className="wit-btn-secondary"
                data-testid="investor-cancel"
              >
                {t("comingSoon.back")}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
